import React, { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import Combobox from '../../components/shared/Combobox';
import { getVehicles, createVehicle, updateVehicle, deleteVehicle } from '../../services/firestore/vehicleService';
import { getVehicleTypes } from '../../services/business/vehicleTypeService';
import { getTransporterDrivers } from '../../services/firestore/driverService';
import '../../styles/components/transporterDashboard.css';

const MyVehicles = ({ userData }) => {
    const { user, isOwner } = usePermissions(userData);
    const { success, error: toastError } = useToast();

    const [loading, setLoading] = useState(true);
    const [vehicles, setVehicles] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [drivers, setDrivers] = useState([]);

    // Modal & Form
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // Dialog State
    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        action: null
    });

    const transporterId = useMemo(() => {
        return userData?.transporterId || userData?.scope?.transporterId ||
            user?.transporterId || user?.scope?.transporterId;
    }, [userData, user]);

    useEffect(() => {
        if (user?.tenantId && transporterId) {
            loadData();
        } else {
            setLoading(false);
        }
    }, [user?.tenantId, transporterId]);

    const loadData = async () => {
        setLoading(true);
        const tid = transporterId?.toString().trim();
        console.log('[MyVehicles] Loading data for:', { tenantId: user.tenantId, transporterId: tid });
        try {
            const [vData, tData, dData] = await Promise.all([
                getVehicles({
                    tenantId: user.tenantId,
                    transporterId: tid
                }),
                getVehicleTypes(user.tenantId),
                getTransporterDrivers(tid, user.tenantId)
            ]);
            setVehicles(vData);
            setVehicleTypes(tData);
            setDrivers(dData);
        } catch (err) {
            console.error('[MyVehicles] Load error:', err);
            console.error('[MyVehicles] Current User Data:', user);
            toastError("Veriler yüklenirken hata oluştu: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const handleOpenModal = () => {
        setSelectedVehicle(null);
        setFormData({
            licensePlate: '',
            vehicleType: vehicleTypes.length > 0 ? vehicleTypes[0].id : '',
            fixedDriver: null,
            driverName: '',
            driverPhone: '',
            driverId: ''
        });
        setIsModalOpen(true);
    };

    const handleEdit = (vehicle) => {
        setSelectedVehicle(vehicle);
        const fixedDriver = drivers.find(d => d.id === vehicle.driver?.id) || null;
        setFormData({
            licensePlate: vehicle.licensePlate || '',
            vehicleType: vehicle.vehicleTypeId || (vehicleTypes.length > 0 ? vehicleTypes[0].id : ''),
            fixedDriver: fixedDriver,
            driverName: vehicle.driver?.name || '',
            driverPhone: vehicle.driver?.phone || '',
            driverId: vehicle.driver?.idNumber || ''
        });
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const selectedType = vehicleTypes.find(t => t.id === formData.vehicleType);

            const payload = {
                tenantId: user.tenantId,
                transporterId: transporterId?.trim(),
                transporterName: user.transporterName || user.fullName || user.email || 'Bilinmeyen Firma',
                licensePlate: formData.licensePlate.toUpperCase(),
                vehicleTypeId: formData.vehicleType,
                vehicleTypeName: selectedType ? selectedType.name : 'Unknown',
                driver: formData.fixedDriver ? {
                    id: formData.fixedDriver.id,
                    name: `${formData.fixedDriver.name} ${formData.fixedDriver.surname}`,
                    phone: formData.fixedDriver.phone,
                    idNumber: formData.fixedDriver.idNumber,
                    photoUrl: formData.fixedDriver.photoUrl
                } : (formData.driverName ? {
                    name: formData.driverName,
                    phone: formData.driverPhone,
                    idNumber: formData.driverId
                } : null),
                status: selectedVehicle ? selectedVehicle.status : 'approved',
                isActive: true
            };

            if (selectedVehicle) {
                // UPDATE
                await updateVehicle(selectedVehicle.id, payload, user.tenantId);
                success('Araç bilgileri güncellendi.');
            } else {
                // CREATE
                await createVehicle(payload, user.tenantId);
                success('Araç başarıyla kaydedildi.');
            }

            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toastError("İşlem başarısız: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (vehicleId, plate) => {
        setConfirmConfig({
            isOpen: true,
            title: 'Aracı Sil',
            message: `${plate} plakalı aracı silmek istediğinize emin misiniz?`,
            type: 'error',
            action: async () => {
                try {
                    await deleteVehicle(vehicleId, user.tenantId);
                    success('Araç silindi.');
                    loadData();
                } catch (err) {
                    toastError("Silme işlemi başarısız.");
                }
            }
        });
    };

    const getStatusBadge = (status) => {
        const normalizedStatus = status === 'approved' ? 'approved' : 'pending';
        const label = status === 'approved' ? 'Onaylı' : 'Beklemede';
        return <span className={`status-badge ${normalizedStatus}`}>{label}</span>;
    };

    if (!transporterId && !isOwner) {
        return (
            <div className="container pt-xl text-center">
                <div className="card-premium p-xl">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: 'var(--text-primary)' }}>Yetki Hatası</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Kullanıcı hesabınız bir Nakliye Firması ile ilişkilendirilmemiş. (Transporter ID Eksik)</p>
                </div>
            </div>
        );
    }

    if (!transporterId && isOwner) {
        return (
            <div className="container p-xl text-center" style={{ color: 'var(--text-primary)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚛</div>
                <h2>Nakliye Bağlantısı Yok</h2>
                <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                    Yönetici olarak bu sayfayı görebilirsiniz ancak herhangi bir firmaya bağlı olmadığınız için
                    araç listesi görüntülenemiyor.
                </p>
            </div>
        );
    }

    return (
        <div className="container pt-lg">
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h1 style={{ color: 'var(--text-primary)' }}>Araçlar</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Kayıtlı araçları ve sürücü listesini yönetin.</p>
                </div>
                <Button variant="primary" onClick={handleOpenModal}>+ Yeni Araç Ekle</Button>
            </div>

            <div className="glass-panel led-glow" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div className="p-xl text-center" style={{ color: 'var(--text-primary)' }}>Yükleniyor...</div>
                ) : vehicles.length === 0 ? (
                    <div className="p-xl text-center" style={{ color: 'var(--text-secondary)' }}>
                        Henüz kayıtlı araç bulunmuyor.
                    </div>
                ) : (
                    <div className="modern-table-container">
                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th>Plaka</th>
                                    <th>Araç Tipi</th>
                                    <th>Sürücü</th>
                                    <th>Telefon</th>
                                    <th>Durum</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map(v => (
                                    <tr key={v.id}>
                                        <td className="plate-cell">
                                            {v.licensePlate}
                                        </td>
                                        <td>{v.vehicleTypeName}</td>
                                        <td>{v.driver?.name || '-'}</td>
                                        <td>
                                            <span className="text-secondary opacity-80">
                                                {v.driver?.phone || '-'}
                                            </span>
                                        </td>
                                        <td>{getStatusBadge(v.status)}</td>
                                        <td>
                                            <div className="flex gap-sm">
                                                <Button size="xs" variant="secondary" onClick={() => handleEdit(v)}>Düzenle</Button>
                                                <Button size="xs" variant="danger" onClick={() => handleDelete(v.id, v.licensePlate)}>Sil</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Vehicle Modal */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedVehicle ? "Araç Düzenle" : "Yeni Araç Kaydı"}>
                <form onSubmit={handleSave} className="p-md">
                    <div className="form-group">
                        <label className="form-label">Plaka</label>
                        <input
                            required
                            type="text"
                            autoComplete="off"
                            className="input w-full"
                            placeholder="Örn: 34ABC123"
                            value={formData.licensePlate}
                            onChange={e => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Araç Tipi</label>
                        <select
                            className="glass-input w-full"
                            value={formData.vehicleType}
                            onChange={e => setFormData({ ...formData, vehicleType: e.target.value })}
                        >
                            {vehicleTypes.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Sabit Sürücü (Opsiyonel)</label>
                        <Combobox
                            items={drivers}
                            value={formData.fixedDriver ? `${formData.fixedDriver.name} ${formData.fixedDriver.surname}` : ''}
                            onChange={(driver) => setFormData({
                                ...formData,
                                fixedDriver: driver,
                                driverName: `${driver.name} ${driver.surname}`,
                                driverPhone: driver.phone,
                                driverId: driver.idNumber
                            })}
                            onInputChange={(val) => {
                                if (!val) setFormData({ ...formData, fixedDriver: null });
                            }}
                            placeholder="Sürücü Listenizden Seçin..."
                            labelKey={(d) => `${d.name} ${d.surname}`}
                            valueKey="id"
                        />
                    </div>

                    {!formData.fixedDriver && (
                        <div style={{ opacity: 0.6, pointerEvents: 'none' }}>
                            <div className="grid grid-cols-2 gap-md">
                                <div className="form-group">
                                    <label className="form-label">Sürücü Ad Soyad</label>
                                    <input
                                        type="text"
                                        className="input w-full"
                                        placeholder="Sabit Sürücü seçilmediyse manuel girilebilir..."
                                        value={formData.driverName}
                                        disabled
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Telefon</label>
                                    <input
                                        type="text"
                                        className="input w-full"
                                        value={formData.driverPhone}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {formData.fixedDriver && (
                        <div className="card-premium p-sm flex items-center gap-md" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', background: 'var(--bg-tertiary)' }}>
                                {formData.fixedDriver.photoUrl ? (
                                    <img src={formData.fixedDriver.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-lg">👤</div>
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{formData.fixedDriver.name} {formData.fixedDriver.surname}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{formData.fixedDriver.phone}</div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-md mt-xl">
                        <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                        <Button type="submit" variant="primary" loading={isSaving}>Kaydet</Button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                {...confirmConfig}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={async () => {
                    if (confirmConfig.action) await confirmConfig.action();
                    setConfirmConfig(prev => ({ ...prev, isOpen: false }));
                }}
            />
        </div>
    );
};

export default MyVehicles;
